import { useEffect, useRef } from 'react';
import { getChatbotConfig } from '../../config/config';
import DataLayerHelper from '../../helpers/tagManager/DataLayerHelper';
import {
  applyChatbotExtraData,
  buildLivenessChatbotExtraData,
  getLivenessIdentityFromHref,
} from './chatbotExtraData';
import { loginChatbotWithSpa } from './chatbotLogin';

const LIVENESS_CHATBOT_CONTENT_TYPE = 'sign-liveness-chatbot-data-af-chat';

let chatbotInstance = null;
let pendingOpen = false;
let getSnapshot = () => ({});

function emitOpen(instance) {
  applyChatbotExtraData(instance, buildLivenessChatbotExtraData(getSnapshot()));
  instance.emit('widgetShow');
  instance.emit('widgetOpened', { isUserOpened: true });
  instance.emit('widgetOpen');
}

export function openLivenessChatbot() {
  if (!chatbotInstance) {
    pendingOpen = true;
    return;
  }
  emitOpen(chatbotInstance);
}

function flushPendingOpen() {
  if (!pendingOpen || !chatbotInstance) {
    return;
  }
  pendingOpen = false;
  emitOpen(chatbotInstance);
}

export function __setChatbotInstanceForTests(instance) {
  chatbotInstance = instance;
  pendingOpen = false;
}

const ChatbotInitializer = ({ chatbotContainerRef, hasError, errorCode }) => {
  const contextRef = useRef({ hasError, errorCode });
  contextRef.current = {
    identity: getLivenessIdentityFromHref(window.location.href),
    hasError,
    errorCode,
  };
  getSnapshot = () => contextRef.current;

  useEffect(() => {
    const chatbot = getChatbotConfig();
    if (!chatbot.enabled) {
      return undefined;
    }

    let cancelled = false;
    let localInstance = null;

    (async () => {
      const targetElement = chatbotContainerRef?.current;
      if (!targetElement) {
        return;
      }

      await import('@autofact-lib/autofact-lib-ts-chatbot/styles');
      if (cancelled) {
        return;
      }

      const { initChatbot } = await import('@autofact-lib/autofact-lib-ts-chatbot');
      if (cancelled) {
        return;
      }

      // hybrid exige setLoginBff; en este SPA no hay BFF, así que recaptcha usa spaLogin del SDK.
      let authStrategy = 'api_key';
      if (chatbot.recaptchaSiteKey) {
        authStrategy = chatbot.authStrategy === 'hybrid' ? 'recaptcha' : chatbot.authStrategy;
      }

      localInstance = initChatbot({
        apiHttpUrl: chatbot.apiHttpUrl,
        apiWsUrl: chatbot.apiWsUrl,
        apiKey: chatbot.apiKey || 'unused-when-recaptcha',
        authStrategy,
        recaptchaSiteKey: chatbot.recaptchaSiteKey,
        bot: chatbot.bot,
        chatName: chatbot.chatName,
        chatAvatarUrl: chatbot.chatAvatarUrl,
        headerColor: chatbot.headerColor,
        footerColor: chatbot.footerColor,
        floatingButtonColor: chatbot.floatingButtonColor,
        sendButtonColor: chatbot.sendButtonColor,
        targetElement,
        position: 'bottom-right',
        showAutomaticallyAttentionMessage: false,
        greetAutomatically: true,
        deferConnectionUntilInteraction: true,
        cornerSpace: chatbot.cornerSpace,
        bottomSpace: chatbot.bottomSpace,
      });

      if (cancelled || !localInstance) {
        return;
      }

      if (authStrategy === 'hybrid' || authStrategy === 'recaptcha') {
        localInstance.setLoginBff((recaptchaToken) => loginChatbotWithSpa(
          chatbot.apiHttpUrl,
          chatbot.apiKey,
          recaptchaToken,
        ));
      }

      chatbotInstance = localInstance;
      chatbotInstance.emit('widgetHide');
      chatbotInstance.on('widgetClosed', () => {
        chatbotInstance.emit('widgetHide');
      });
      chatbotInstance.on('widgetOpened', () => {
        DataLayerHelper.pushEvent('select_content', {
          content_type: LIVENESS_CHATBOT_CONTENT_TYPE,
          content_id: `${LIVENESS_CHATBOT_CONTENT_TYPE}-open`,
        });
      });
      chatbotInstance.on('messageSent', (data) => {
        if (data?.message) {
          DataLayerHelper.pushEvent('select_content', {
            content_type: LIVENESS_CHATBOT_CONTENT_TYPE,
            content_id: `${LIVENESS_CHATBOT_CONTENT_TYPE}-sent`,
          });
        }
      });
      applyChatbotExtraData(chatbotInstance, buildLivenessChatbotExtraData(contextRef.current));
      flushPendingOpen();
    })();

    return () => {
      cancelled = true;
      if (localInstance?.webSocketService) {
        localInstance.webSocketService.disconnect();
      }
      if (chatbotInstance === localInstance) {
        chatbotInstance = null;
      }
    };
  }, [chatbotContainerRef]);

  useEffect(() => {
    if (!chatbotInstance) {
      return;
    }
    applyChatbotExtraData(chatbotInstance, buildLivenessChatbotExtraData(contextRef.current));
  }, [hasError, errorCode]);

  return null;
};

export default ChatbotInitializer;
